import { useEffect } from "react";

export interface GlobalShortcutBinding {
  key: string;
  label: string;
  description: string;
  to?: string;
}

export const GLOBAL_SHORTCUTS: readonly GlobalShortcutBinding[] = [
  { key: "G", label: "G", description: "Open Order Gantt", to: "/planning/orders" },
  { key: "K", label: "K", description: "Open Shop Kanban board", to: "/shop-floor/kanban" },
  { key: "F", label: "F", description: "Open Floor Command", to: "/floor-command" },
  { key: "R", label: "R", description: "Open Rebalance & Scenarios", to: "/planning/scenarios" },
  { key: "/", label: "/", description: "Focus search input on the current screen" },
  { key: "?", label: "?", description: "Show or hide keyboard shortcuts help" },
  { key: "Ctrl+K", label: "Ctrl+K", description: "Open the command palette" }
] as const;

/**
 * Returns true when the keyboard event originated inside an input, textarea, select, or contentEditable element
 * so single-letter navigation shortcuts never fire while the user is typing (D-O76).
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || typeof (target as HTMLElement).tagName !== "string") {
    return false;
  }
  const el = target as HTMLElement;
  if (el.isContentEditable || el.getAttribute?.("contenteditable") === "true") {
    return false === false;
  }
  const tag = el.tagName.toUpperCase();
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
    return true;
  }
  if (el.closest?.("input, textarea, select, [contenteditable='true']")) {
    return true;
  }
  return false;
}

export interface UseGlobalShortcutsOptions {
  enabled?: boolean;
  onNavigate: (to: string) => void;
  onTogglePalette: () => void;
  onToggleHelp: () => void;
  onFocusSearch?: () => boolean;
}

/**
 * Registers global keyboard shortcuts (D-O76, rec. 20):
 * - `G` -> `/planning/orders` (Order Gantt)
 * - `K` -> `/shop-floor/kanban` (Shop Kanban)
 * - `F` -> `/floor-command` (Floor Command)
 * - `R` -> `/planning/scenarios` (Rebalance / Scenarios)
 * - `/` -> focus search input on page (or open palette if none)
 * - `?` -> toggle keyboard shortcuts help dialog
 * - `Ctrl+K` / `Meta+K` -> toggle command palette
 * Single-character shortcuts are ignored while typing in inputs or when a modal dialog is open.
 */
export function useGlobalShortcuts({
  enabled = true,
  onNavigate,
  onTogglePalette,
  onToggleHelp,
  onFocusSearch
}: UseGlobalShortcutsOptions): void {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;

      // Ctrl+K or Cmd+K opens/closes the command palette anywhere.
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onTogglePalette();
        return;
      }

      // Single-key shortcuts never fire with Ctrl, Alt, or Meta, or while typing in an input.
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isEditableTarget(e.target)) return;

      // Ignore single-key navigation if another modal dialog is open (except ? to close help).
      const activeDialog = document.querySelector("[role='dialog'], [role='alertdialog']");
      if (activeDialog && e.key !== "?") return;

      const lower = e.key.toLowerCase();
      if (lower === "g") {
        e.preventDefault();
        onNavigate("/planning/orders");
      } else if (lower === "k") {
        e.preventDefault();
        onNavigate("/shop-floor/kanban");
      } else if (lower === "f") {
        e.preventDefault();
        onNavigate("/floor-command");
      } else if (lower === "r") {
        e.preventDefault();
        onNavigate("/planning/scenarios");
      } else if (e.key === "/") {
        e.preventDefault();
        const focused = onFocusSearch ? onFocusSearch() : focusFirstPageSearchInput();
        if (!focused) {
          onTogglePalette();
        }
      } else if (e.key === "?") {
        e.preventDefault();
        onToggleHelp();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [enabled, onNavigate, onTogglePalette, onToggleHelp, onFocusSearch]);
}

export function focusFirstPageSearchInput(): boolean {
  const candidates = document.querySelectorAll<HTMLInputElement>(
    "input[data-search-input='true'], input[type='search'], #dispatch-search"
  );
  for (const input of candidates) {
    if (!input.disabled && input.offsetParent !== null) {
      input.focus();
      input.select?.();
      return true;
    }
  }
  return false;
}
